import { Group, Loader, Text } from "@mantine/core";
import { useEffect, useRef } from "react";

import { isApiError } from "../../api/errors";
import { useGetAsset } from "../../api/generated/endpoints/assets/assets";
import { ErrorAlert } from "../../platform/ErrorAlert";
import classes from "./library.module.css";
import { savingPollInterval, useSavingReads } from "../../platform/media/saving-poll";

export interface SavingCardProps {
  teamId: string;
  brandId: string;
  assetId: string;
  /**
   * Called once, when the asset is ready: it reads the lists again, then drops this card, so the
   * file is on screen in one place or the other throughout.
   */
  onReady: () => void;
}

/**
 * A job output the server is still copying into the library (S1: the save answers `pending`, a
 * worker makes it `ready`). The list holds ready files only, so this reads the asset by id every
 * 2 s, for about a minute, and hands over to the list once it is ready.
 */
export function SavingCard({ teamId, brandId, assetId, onReady }: SavingCardProps) {
  const asset = useGetAsset(teamId, brandId, assetId, {
    query: {
      retry: false,
      refetchInterval: (query) =>
        savingPollInterval(query.state.data?.status, query.state.dataUpdateCount),
    },
  });
  const status = asset.data?.status;
  const handedOver = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (status !== "ready" || handedOver.current === assetId) return;
    handedOver.current = assetId;
    onReady();
  }, [status, assetId, onReady]);

  const name = asset.data?.name ?? "the file";
  const reads = useSavingReads(asset, teamId, brandId, assetId);
  const polling = savingPollInterval(status, reads) !== false;
  // Ready: shown as saving until the lists, read again, hold the file and this card goes.
  const busy = asset.isPending || polling || status === "ready";
  // A failed save is purged soon after, so its row may be gone by the time this reads it.
  const gone = isApiError(asset.error, "NOT_FOUND");
  let line: string;
  if (gone) {
    line = "This save is no longer there. Try saving it again from its job.";
  } else if (status === "failed") {
    line = `${name} could not be saved. Try again from its job.`;
  } else if (busy) {
    line = `Saving ${name}…`;
  } else {
    line = `Still saving ${name}. It appears here when it is ready.`;
  }
  // The masonry's first card: a soft box at the output's place, with what is happening below it.
  return (
    <div className={classes.saving}>
      <div className={classes.savingBox} />
      {asset.isError && !gone ? (
        <ErrorAlert error={asset.error} />
      ) : (
        <Group gap="xs" wrap="nowrap">
          {busy ? <Loader size="xs" aria-hidden /> : null}
          <Text component="output" size="sm">
            {line}
          </Text>
        </Group>
      )}
    </div>
  );
}
